import { useErrorStore } from '@/stores/errorStore'
import { getToken } from '@/services/tokenService'

const BASE_URL = import.meta.env.VITE_API_BASE_URL

export interface BookingParams {
  token: string
  userId: string
  userEmail: string
  includeCancelledBookings?: boolean
  includeCancelledSegments?: boolean
  pageSize?: number
  fromPage?: number
  sort?: string
  travelStartDate?: string
  travelEndDate?: string
  url?: string
}

export interface BookingDetailsParams {
  token: string
  bookingIdentifier: string
  excludeCancelledItems?: boolean
  excludeExpiredItems?: boolean
}

/**
 * Fetches bookings for a user
 * @param params - Parameters for the bookings request
 */
export const getBookings = async (params: BookingParams): Promise<any> => {
  const { 
    token, 
    userId, 
    userEmail, 
    includeCancelledBookings = false, 
    includeCancelledSegments = false,
    pageSize = 20,
    fromPage = 0,
    sort = '-schedule_date',
    travelStartDate,
    travelEndDate,
    url: customUrl 
  } = params

  if (!token) {
    throw new Error('No token available')
  }

  let url = customUrl
  if (!url) {
    if (!userId || !userEmail) {
      throw new Error('User ID and email are required')
    }
    
    const paramsObj: Record<string, string> = {
      crm_reference: userId,
      customer_email: userEmail,
      sort,
      include_cancelled_bookings: includeCancelledBookings.toString(),
      page_size: pageSize.toString(),
      from_page: fromPage.toString()
    }
    
    if (includeCancelledSegments) {
      paramsObj.include_cancelled_segments = 'true'
    }
    
    if (travelStartDate) {
      paramsObj.travel_start_date = travelStartDate
    }
    
    if (travelEndDate) {
      paramsObj.travel_end_date = travelEndDate
    }
    
    const queryParams = new URLSearchParams(paramsObj)
    url = `${BASE_URL}/api/v3/bookings?${queryParams.toString()}`
  }

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    })

    const contentType = response.headers.get('content-type')
    if (!response.ok) {
      if (contentType?.includes('application/json')) {
        const responseData = await response.json()
        console.error('API error response:', responseData)
        throw new Error(
          responseData.error?.message || 'Failed to fetch bookings'
        )
      } else {
        const text = await response.text()
        console.error('API error response (non-JSON):', text)
        throw new Error('Failed to fetch bookings')
      }
    }

    if (!contentType?.includes('application/json')) {
      const text = await response.text()
      console.error('Unexpected response type:', contentType, 'Response:', text)
      throw new Error('Unexpected response type from server')
    }

    const data = await response.json()
    if (!data || !data.bookings) {
      console.error('Invalid response format:', data)
      throw new Error('Invalid response format from server')
    }
    console.log('API Response:', {
      url,
      bookingsCount: data.bookings.length,
      totalCount: data.total_count,
      nextPage: data.next_page,
      prevPage: data.prev_page
    })
    return data
  } catch (error) {
    console.error('Error fetching bookings:', error)
    throw error
  }
}

/**
 * Fetches detailed booking information by booking identifier (booking number or UUID)
 * @param params - Parameters for the booking details request
 */
export const getBookingDetails = async (params: BookingDetailsParams): Promise<any> => {
  const {
    token,
    bookingIdentifier,
    excludeCancelledItems = true,
    excludeExpiredItems = true
  } = params

  if (!token) {
    throw new Error('No token available')
  }

  if (!bookingIdentifier) {
    throw new Error('Booking identifier is required')
  }

  // Build URL with query parameters
  let url = `${BASE_URL}/api/v2/booking/${encodeURIComponent(bookingIdentifier)}`
  const excludeParams = []
  if (excludeCancelledItems) excludeParams.push('exclude[]=cancelled_items')
  if (excludeExpiredItems) excludeParams.push('exclude[]=expired_items')
  if (excludeParams.length > 0) url += `?${excludeParams.join('&')}`

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    })

    const contentType = response.headers.get('content-type')
    if (!response.ok) {
      if (contentType?.includes('application/json')) {
        const responseData = await response.json()
        console.error('API error response for booking details:', responseData)
        throw new Error(
          responseData.error?.message || `Failed to fetch details for booking ${bookingIdentifier}`
        )
      } else {
        const text = await response.text()
        console.error('API error response for booking details (non-JSON):', text)
        throw new Error(`Failed to fetch details for booking ${bookingIdentifier}`)
      }
    }

    if (!contentType?.includes('application/json')) {
      const text = await response.text()
      console.error('Unexpected response type:', contentType, 'Response:', text)
      throw new Error('Unexpected response type from server')
    }

    const data = await response.json()
    if (!data || !data.data || !data.data.booking) {
      console.error('Invalid booking details response format:', data)
      throw new Error('Invalid response format from server')
    }
    
    console.log('Booking details API Response:', {
      bookingIdentifier,
      hasDetails: !!data.data.booking
    })
    
    return data
  } catch (error) {
    console.error(`Error fetching details for booking ${bookingIdentifier}:`, error)
    throw error
  }
}


